import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';
import { decodeFuelType, formatPrice } from '../constants/carFilters';
import { applyTemplate } from '../utils/smsFormat';

const MAX_MESSAGE = 100;
const box = { background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #eee' };

/**
 * PWA "1. lastnik" — vozila fizičnih oseb do 100.000 km, 1 lastnik, slovensko poreklo, brez okvare.
 * Prodajalcu lahko pošlješ SMS. Vir: GET /cars/first-owner. Zrcali EndUserDamaged (/app lupina).
 */
export default function EndUserFirstOwner() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [messages, setMessages] = useState({});
  const [sendStatus, setSendStatus] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [carsRes, profileRes] = await Promise.all([
        apiService.getFirstOwnerCars(168),
        apiService.getSmsProfile().catch(() => null),
      ]);
      setCars((carsRes && carsRes.data) || []);
      if (profileRes && profileRes.data) setProfile(profileRes.data);
    } catch (e) {
      /* non-critical */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSend = async (car) => {
    const raw = (messages[car.id] !== undefined ? messages[car.id] : (profile && profile.defaultSmsMessage) || '').trim();
    if (!raw) {
      setSendStatus((s) => ({ ...s, [car.id]: { type: 'error', text: 'Sporočilo je prazno (nastavi privzeto v SMS plošči).' } }));
      return;
    }
    setSendStatus((s) => ({ ...s, [car.id]: { type: 'sending', text: 'Pošiljam …' } }));
    try {
      const res = await apiService.sendSms({
        avtonetId: car.avtonetId,
        recipientPhone: car.phone,
        message: applyTemplate(raw, car),
        filterUsed: '1. lastnik',
      });
      const ok = res.data && res.data.sent;
      setSendStatus((s) => ({ ...s, [car.id]: { type: ok ? 'ok' : 'warn', text: ok ? '✓ Poslano' : ('⚠ ' + ((res.data && (res.data.error || res.data.note)) || 'ni poslano')) } }));
    } catch (e) {
      const msg = (e.response && e.response.data && e.response.data.error) || 'Napaka';
      setSendStatus((s) => ({ ...s, [car.id]: { type: 'error', text: '✗ ' + msg } }));
    }
  };

  return (
    <>
      <div style={{ ...box, padding: 14, marginBottom: 14 }}>
        <div style={{ fontWeight: 600, color: '#222' }}>1. lastnik — vozila fizičnih oseb</div>
        <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
          Do 100.000 km, 1 lastnik, slovensko poreklo (kupljen v SLO), brez okvare. Prodajalcu lahko pošlješ SMS.
        </div>
      </div>

      {loading ? (
        <div style={{ ...box, padding: 16, color: '#666' }}>Nalagam…</div>
      ) : cars.length === 0 ? (
        <div style={{ ...box, padding: 20, textAlign: 'center', color: '#666' }}>
          Trenutno ni vozil, ki bi ustrezala pogojem (do 100.000 km, 1 lastnik, SLO poreklo).
        </div>
      ) : (
        cars.map((car) => {
          const msg = messages[car.id] !== undefined ? messages[car.id] : ((profile && profile.defaultSmsMessage) || '');
          const status = sendStatus[car.id];
          const title = `${car.znamka || ''} ${car.model || ''}`.trim() || 'Vozilo';
          const href = car.avtonetId ? `https://www.avto.net/Ads/details.asp?id=${car.avtonetId}` : undefined;
          return (
            <div key={car.id} style={{ ...box, padding: 14, marginBottom: 12 }}>
              <a href={href} target="_blank" rel="noreferrer" style={{ fontSize: 17, fontWeight: 600, color: '#222', textDecoration: 'none' }}>{title}</a>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#007AFF', marginTop: 2 }}>{formatPrice(car.cena)}</div>
              <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                {[car.letnik && `📅 ${car.letnik}`, car.mileage && `🛣️ ${car.mileage}`, car.gorivo && `⛽ ${decodeFuelType(car.gorivo)}`, car.viewLocation && `📍 ${car.viewLocation}`].filter(Boolean).join('   ·   ')}
              </div>
              <div style={{ marginTop: 4 }}>
                <span style={{ display: 'inline-block', background: '#198754', color: '#fff', borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600, marginRight: 6, marginTop: 4 }}>1. lastnik</span>
                <span style={{ display: 'inline-block', background: '#0d6efd', color: '#fff', borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600, marginRight: 6, marginTop: 4 }}>SLO poreklo</span>
              </div>
              <div style={{ marginTop: 8, fontWeight: 600, color: '#007AFF' }}>📞 {car.phone || '—'}</div>

              <textarea
                value={msg}
                maxLength={MAX_MESSAGE}
                rows={2}
                onChange={(e) => setMessages((m) => ({ ...m, [car.id]: e.target.value }))}
                placeholder="Sporočilo za prodajalca (do 100 znakov)"
                style={{ width: '100%', padding: 8, marginTop: 8, boxSizing: 'border-box', borderRadius: 8, border: '1px solid #ddd', fontSize: 14 }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                <span style={{ fontSize: 12, color: '#888' }}>{msg.length}/{MAX_MESSAGE}</span>
                {status ? <span style={{ fontSize: 12, color: status.type === 'error' ? '#d00' : status.type === 'ok' ? '#080' : '#a60' }}>{status.text}</span> : null}
              </div>
              <button
                onClick={() => handleSend(car)}
                disabled={!car.phone || (status && status.type === 'sending')}
                style={{ width: '100%', marginTop: 8, padding: '12px', border: 'none', borderRadius: 10, background: '#34C759', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', opacity: !car.phone ? 0.5 : 1 }}
              >
                💬 Pošlji SMS
              </button>
            </div>
          );
        })
      )}
    </>
  );
}
